import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { onSearchModal, publishSearchModalShown, publishSearchModalMode } from "../utils/searchModalSignal.js";
import { fetchItems, getCachedItems } from "../utils/inventoryWarmup.js";
import { formatDisplayPrice } from "../utils/currency.js";
import { HIT_COUNT, SETTLE_MS, coverOf, rankHits } from "../utils/searchHits.js";
import { isMobileViewport } from "../utils/viewport.js";
import { lockScroll, unlockScroll } from "../utils/scrollLock.js";
import { peekPendingBrandNav, requestBrandNav } from "../utils/brandNavSignal.js";
import { fetchBrandList, peekBrandList, preloadImage, waitForBrandImages } from "../utils/brandList.js";

// On a cold open the tiles wait at most this long, after the list has come,
// for their backdrops, so most arrive with their pictures; any still on the
// way then fade in as they land.
const TILE_IMAGE_WAIT_MS = 400;
// Until the list is in, the glass holds this many plain tiles where the
// brands will be, so it is never empty (the owner: "sometimes showcase fails
// to load"); the same tiles then take the brands.
const PLACEHOLDER_TILES = 8;

// The site search: the sidebar's own glass, over the whole page instead of
// a panel (the page behind blurred and dimmed), with one centred bar. It is
// opened from the header's search icon only (utils/searchModalSignal), and
// it behaves exactly as the sidebar's search row does:
//   - the same type: uppercase, bold, grey while typing, a white caret;
//   - on Enter (or the arrow) the search navigates at once, in the
//     background, while the typed word fills from grey to white left to
//     right, its underline filling in step (the --search-fill custom
//     property, in random jumps over at least 1.5s), and only then does the
//     modal close over the results;
//   - on a phone there is no fill: navigate and close at once;
//   - opening on the search page prefills the current query;
//   - Escape clears and closes.
// While there is text, the items it matches so far show under the bar (up
// to HIT_COUNT, title matches first, newest first), each a link straight to
// the product. The match is the catalog search's own: the text anywhere in
// the title, brand, category, code, colour or description.
// "Search" sits in the empty field; an arrow appears at the end of
// the line once there is something to search. Escape, the X, or a click on the glass closes it.
// The desktop header's SHOWCASE opens the same overlay in its brands mode:
// the same glass, header and X, and in place of the bar the showcase brands
// as the home page's SHOWCASE tiles (backdrop and name, no count, the name
// at the big size), three to a row, coming in one at a time, with ALL
// BRANDS last. It opens over the showcase itself: from any other page, the
// page behind goes to /showcase, on a random brand. Pointing at a name shows
// that brand behind the glass; pressing it closes the list there, and ALL
// BRANDS just closes it. Phones never get it: their header has no SHOWCASE link.
// Opening is two beats: the glass lands at once, then the bar fades in (the
// CSS staggers them off one class). Closing is one: glass, bar and matches
// fade together. The bar keeps one place, the middle of the screen,
// whatever the matches do; the list scrolls in the half screen beneath it.

// How long the close fade takes in CSS (.search-modal--closing); the node
// stays mounted that long so it can fade out.
const CLOSE_MS = 260;
// The fill's floor, as in the sidebar: even a fast fill holds this long,
// plus 400ms, before the modal closes.
const FILL_MIN_MS = 1500;
// How long the pointer must be down before the glass clears. Long enough that
// an ordinary click to close never flashes the page through on its way out,
// short enough that a deliberate hold feels immediate.
const WIPE_HOLD_MS = 150;

// The shared phone breakpoint (utils/viewport), so this flips with the CSS.
const isPhone = isMobileViewport;



export default function SearchModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState(() => getCachedItems() || null);
  const inputRef = useRef(null);
  // "search" (the bar) or "brands" (the showcase list). A ref as well, for
  // the signal handler, which is armed once.
  const [mode, setMode] = useState("search");
  const modeRef = useRef("search");
  modeRef.current = mode;
  const [brands, setBrands] = useState([]);
  // Whether the list has settled at least once (even empty), and which tile
  // backdrops have loaded (their tiles show the picture; the rest fade it in
  // when it lands).
  const [brandsSettled, setBrandsSettled] = useState(false);
  const [loadedTileUrls, setLoadedTileUrls] = useState(() => new Set());
  const brandsRef = useRef(null);

  // The matches scroll with no scrollbar at rest; one fades in only while a
  // scroll is actually happening, then fades back out. The class is toggled
  // straight on the node rather than through state: this fires on every
  // scroll frame, and a re-render per frame would cost far more than the
  // hint is worth.
  const listRef = useRef(null);
  const scrollIdleRef = useRef(null);
  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.classList.add("search-modal__list--scrolling");
    clearTimeout(scrollIdleRef.current);
    scrollIdleRef.current = setTimeout(() => {
      el.classList.remove("search-modal__list--scrolling");
    }, 700);
  }, []);
  useEffect(() => () => clearTimeout(scrollIdleRef.current), []);
  const formRef = useRef(null);
  const closeTimer = useRef(null);
  const fillTimer = useRef(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  // Closing is one fade: the container carries glass, bar and matches out
  // together (--closing) while --open stays on, so nothing moves underneath
  // it; everything resets once the node is gone.
  // ── Wipe the glass ──
  // Press and hold anywhere on the overlay and the frost lifts, so you can
  // read the page you came from without leaving the search. Let go and it
  // fogs back over about a second (the asymmetry is in app.css: clearing is
  // instant, returning is slow, the way breath clears and creeps back).
  const [wiped, setWiped] = useState(false);
  // A ref as well as state: the scrim's click handler has to know whether this
  // gesture was a wipe, and reading state there would see the value from the
  // render the press began in.
  const wipedRef = useRef(false);
  const wipeTimer = useRef(null);

  const startWipe = useCallback((e) => {
    // Secondary buttons open menus; and a press inside the field is someone
    // placing a cursor or selecting text, not cleaning the glass.
    if (e.button !== 0) return;
    if (e.target.closest?.(".search-modal__input, .search-modal__brand")) return;
    wipedRef.current = false;
    clearTimeout(wipeTimer.current);
    // Held, not tapped: without this every click to close would flash the
    // page through the glass on its way out.
    wipeTimer.current = setTimeout(() => {
      wipedRef.current = true;
      setWiped(true);
    }, WIPE_HOLD_MS);
  }, []);

  const endWipe = useCallback(() => {
    clearTimeout(wipeTimer.current);
    setWiped(false);
  }, []);

  useEffect(() => () => clearTimeout(wipeTimer.current), []);

  // The showcase list's trip to /showcase (the signal handler below): set
  // while the list is up, from the ask to the close, since the trip waits on
  // the brand list; and set for the navigation the trip makes, so the
  // route-change close further down lets that one through.
  const brandsUpRef = useRef(false);
  const showcaseNavRef = useRef(false);
  const close = useCallback(() => {
    brandsUpRef.current = false;
    clearTimeout(fillTimer.current);
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setMounted(false);
      setOpen(false);
      setClosing(false);
      setLoading(false);
      setQuery("");
    }, CLOSE_MS);
  }, []);

  // Where the modal stands, for the signal handler below, which is armed
  // once and would otherwise see the state of the render it was armed in.
  const shownRef = useRef(false);
  shownRef.current = mounted && !closing;

  // The header's search icons toggle the search, and the desktop header's
  // SHOWCASE ("brands") toggles the showcase list; anything else may open or
  // close. Either one pressed while the other is up switches the overlay over
  // to it rather than closing it.
  useEffect(() => onSearchModal((action) => {
    const shown = shownRef.current;
    const brandsWanted = action === "brands";
    const want = brandsWanted ? "brands" : "search";
    if (action === "close" || ((action === "toggle" || brandsWanted) && shown && modeRef.current === want)) {
      if (shown) close();
      return;
    }
    clearTimeout(closeTimer.current);
    clearTimeout(fillTimer.current);
    setLoading(false);
    setClosing(false);
    setMode(want);
    modeRef.current = want;
    brandsUpRef.current = brandsWanted;
    if (brandsWanted) {
      // A list already in hand shows at once; the fetch below refreshes it.
      const known = peekBrandList();
      if (known) {
        setBrands(known);
        setBrandsSettled(true);
      }
      // The showcase's own list (utils/brandList): alphabetical, and only
      // brands with something live, so every name has a panel to land on.
      // The list opens over the showcase itself: from any other page, the
      // page behind goes to /showcase under the glass, on a random brand, so
      // pointing at a name can show that brand there (previewBrand) and ALL
      // BRANDS has only to close. A name pointed at before the page arrives is
      // parked on the channel and wins over the roll.
      fetchBrandList().then((list) => {
        // Each backdrop, as it lands, is marked so its tile shows it.
        (list || []).forEach((b) => {
          if (!b.url) return;
          preloadImage(b.url).then(() => {
            setLoadedTileUrls((prev) => (prev.has(b.url) ? prev : new Set(prev).add(b.url)));
          });
        });
        return waitForBrandImages(list, TILE_IMAGE_WAIT_MS).then(() => list);
      }).then((list) => {
        setBrands(list || []);
        setBrandsSettled(true);
        if (!list?.length || !brandsUpRef.current) return;
        if (locationRef.current.pathname === "/showcase") return;
        if (!peekPendingBrandNav()) {
          requestBrandNav(list[Math.floor(Math.random() * list.length)].name, { jump: true });
        }
        showcaseNavRef.current = true;
        navigate("/showcase");
      }).catch(() => {});
    } else {
      // As the sidebar does: on the search page, start from the current query.
      const loc = locationRef.current;
      const current = loc.pathname === "/featured/search"
        ? new URLSearchParams(loc.search).get("q") || ""
        : "";
      setQuery(current);
      // The live matches need the inventory; the warm cache usually has it.
      fetchItems().then((list) => setItems(list || [])).catch(() => {});
    }
    setMounted(true);
    // Mount first, flip to --open on a later frame: a node inserted
    // already-open does not animate (the same nuance as the nav fade).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOpen(true);
        if (brandsWanted) {
          brandsRef.current?.querySelector("button")?.focus({ preventScroll: true });
          return;
        }
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.select();
        }
      });
    });
  }), [close, navigate]);

  // The list sits over the showcase itself (the handler above takes the page
  // there as it opens). Pointing at a name shows that brand on the page
  // behind, landed in place, through the channel the sidebar's brand list
  // uses; pressing it closes the list on the brand already there. ALL BRANDS
  // just closes it, on whichever brand is showing. Anywhere else (the trip
  // not made), a pick still goes to its brand and ALL BRANDS opens the deck.
  const isOnShowcase = useCallback(
    () => locationRef.current.pathname === "/showcase" || showcaseNavRef.current,
    [],
  );
  // The pointer previews a name the moment it arrives on it. (It used to
  // wait 90ms for the pointer to rest, so a sweep across the list would not
  // show every brand it passed; the owner, 2026-09-30: "no pause". A sweep
  // now shows each, each crossfade overtaking the last.)
  // The brand last put behind the glass from here, by a name or by ALL
  // BRANDS (previewRandomBrand skips it).
  const lastPreviewed = useRef(null);
  const previewBrand = useCallback((name) => {
    if (!isOnShowcase()) return;
    // The brand already behind the glass: nothing to do. Swapping the page
    // makes the browser re-fire enter on a pointer that has not moved, and
    // that second call restarted the crossfade.
    if (name === lastPreviewed.current) return;
    lastPreviewed.current = name;
    requestBrandNav(name, { jump: true });
  }, [isOnShowcase]);
  // ALL BRANDS, pointed at, shows a brand picked at random behind the glass,
  // a different one each time (never the one just shown): the whole
  // showcase, a page at a time (the owner, 2026-10-17: "everytime we hover
  // over all brands, make it show a random page").
  // Whether the pointer is on ALL BRANDS now (one pick per visit, below).
  const allBrandsVisit = useRef(false);
  const previewRandomBrand = useCallback(() => {
    const names = brands.map((b) => b.name).filter((n) => n !== lastPreviewed.current);
    if (!names.length) return;
    previewBrand(names[Math.floor(Math.random() * names.length)]);
  }, [brands, previewBrand]);
  const goToBrand = useCallback((name) => {
    requestBrandNav(name, { jump: true });
    if (!isOnShowcase()) navigate("/showcase");
    close();
  }, [isOnShowcase, navigate, close]);
  const viewAllBrands = useCallback(() => {
    if (!isOnShowcase()) navigate("/showcase");
    close();
  }, [isOnShowcase, navigate, close]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    clearTimeout(fillTimer.current);
  }, []);

  // While the search is up, the site header stays crisp above the glass
  // (body.search-modal-open in app.css) and is still the way on: pressing
  // anything in it, the bar, the hamburger or the corner group, closes the
  // search first. Its own search icons are left alone; they toggle through
  // the signal above.
  useEffect(() => {
    if (!mounted) return undefined;
    document.body.classList.add("search-modal-open");
    const onDown = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      // The search icons and SHOWCASE toggle the overlay themselves.
      if (t.closest('[data-header-interaction="search"], [data-header-interaction="nav-brand"]')) return;
      if (t.closest(".app-header, .sidebar-toggle, .corner-actions")) close();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.body.classList.remove("search-modal-open");
    };
  }, [mounted, close]);

  // The page behind stays put while the search is up. Without this a swipe
  // on the glass scrolled the page underneath on iOS (utils/scrollLock).
  useEffect(() => {
    if (!mounted) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [mounted]);

  // Tell the header which face is up, the search or the showcase list (the
  // desktop header lights SHOWCASE for the list). Ahead of the effect below,
  // so an opening publishes its face before it counts as shown.
  useEffect(() => {
    publishSearchModalMode(mode);
  }, [mode]);

  // Tell the header whether the search is on screen (its phone search icon
  // becomes the close control while it is). Cleared on unmount too.
  useEffect(() => {
    publishSearchModalShown(mounted && !closing);
  }, [mounted, closing]);
  useEffect(() => () => publishSearchModalShown(false), []);

  // A route change from anywhere (a header link, the browser's back button)
  // closes the search, except the one a submit makes itself: that one
  // navigates while the fill runs over the results, and closes when done.
  // The showcase list's own trip to /showcase is let through as well.
  const loadingRef = useRef(false);
  loadingRef.current = loading;
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === lastPathRef.current) return;
    lastPathRef.current = location.pathname;
    const ownTrip = showcaseNavRef.current && location.pathname === "/showcase";
    showcaseNavRef.current = false;
    if (ownTrip) return;
    if (shownRef.current && !loadingRef.current) close();
  }, [location.pathname, close]);

  // The sidebar's white fill, verbatim: random jumps toward 100% every
  // 20-100ms, then a hold so the whole thing lasts at least FILL_MIN_MS
  // plus 400ms, then done. The property is set on the form so the text and
  // its underline (both read it) fill together.
  const runFill = useCallback((onDone) => {
    const el = formRef.current;
    if (!el) {
      fillTimer.current = setTimeout(onDone, FILL_MIN_MS);
      return;
    }
    let fill = 0;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const remaining = 100 - fill;
      const jump = Math.random() * Math.max(1, remaining * 0.15) + 0.5;
      fill = Math.min(fill + jump, 100);
      el.style.setProperty("--search-fill", fill + "%");
      if (fill < 100) {
        fillTimer.current = setTimeout(tick, Math.random() * 80 + 20);
      } else {
        const wait = Math.max(400, FILL_MIN_MS - elapsed + 400);
        fillTimer.current = setTimeout(onDone, wait);
      }
    };
    tick();
  }, []);

  const normalized = query.trim().toLowerCase();
  const hasQuery = normalized.length > 0;

  // The term the matches are actually drawn from — it trails what is typed by
  // SETTLE_MS. Emptying the box clears instantly: there is nothing to settle
  // on, and holding a spinner over an empty field reads as a hang.
  const [settled, setSettled] = useState("");
  const settling = hasQuery && settled !== normalized;

  useEffect(() => {
    if (!hasQuery) {
      setSettled("");
      return undefined;
    }
    const t = setTimeout(() => setSettled(normalized), SETTLE_MS);
    return () => clearTimeout(t);
  }, [normalized, hasQuery]);

  // Ranking lives in utils/searchHits so this and the home page's bar cannot
  // disagree about what a term matches.
  const hits = useMemo(() => rankHits(items, settled), [items, settled]);

  const submit = (e) => {
    e.preventDefault();
    if (loading) return;
    const term = query.trim();
    if (!term) return;
    const to = `/featured/search?q=${encodeURIComponent(term)}`;
    if (isPhone()) {
      navigate(to);
      close();
      return;
    }
    // Navigate in the background; the fill runs over the top, then close.
    setLoading(true);
    if (formRef.current) formRef.current.style.setProperty("--search-fill", "0%");
    navigate(to);
    runFill(close);
  };

  if (!mounted) return null;
  const showHits = hasQuery && !loading && !settling;

  return (
    <div
      className={`search-modal${open ? " search-modal--open" : ""}${closing ? " search-modal--closing" : ""}${wiped ? " search-modal--wiped" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={mode === "brands" ? "Showcase" : "Search"}
      onPointerDown={startWipe}
      onPointerUp={endWipe}
      onPointerCancel={endWipe}
      onPointerLeave={endWipe}
    >
      {/* The glass is itself the way out: a click anywhere off the bar closes.
          Unless the press was a wipe — holding to see through it and letting
          go would otherwise close the search you were looking past. */}
      <button
        type="button"
        className="search-modal__scrim"
        aria-label={mode === "brands" ? "Close showcase" : "Close search"}
        onClick={() => { if (!wipedRef.current) close(); }}
        tabIndex={-1}
      />
      <button type="button" className="search-modal__close" aria-label={mode === "brands" ? "Close showcase" : "Close search"} onClick={close}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="square" aria-hidden="true" focusable="false">
          <path d="M5 5l14 14M19 5L5 19" />
        </svg>
      </button>
      <div className={`search-modal__panel${mode === "brands" ? " search-modal__panel--brands" : ""}`}>
        {mode === "brands" ? (
          // The showcase brands as the home page's SHOWCASE tiles (the same
          // .home-tile classes: backdrop, bottom fade, name bottom left) but
          // with no count; three to a row, left to right, ALL BRANDS last as
          // the home page's own plain tile, its words where a brand's name
          // sits. Each comes in a beat after the one
          // before (--i, app.css). A brand whose backdrop is a video has no
          // still to paint, so its tile is plain, as on the home page.
          <nav className="search-modal__brands" aria-label="Showcase brands" ref={brandsRef}>
            {/* One run of tiles, keyed by place. Until the list is in, plain
                tiles hold the brands' places (PLACEHOLDER_TILES), so the glass
                is never empty; then the same tiles take the brands, and ALL
                BRANDS last. Same element, same key, so nothing remounts: the
                entrance runs once, and each name and backdrop fades in onto a
                tile already there. Once the list has settled, even empty (the
                inventory could not be had), ALL BRANDS is there, so the way to
                the showcase is never missing from the glass. */}
            {(brands.length === 0 && !brandsSettled
              ? Array.from({ length: PLACEHOLDER_TILES }, (_, i) => ({ kind: "placeholder", i }))
              : [
                ...brands.map((brand, i) => ({ kind: "brand", brand, i })),
                { kind: "all", i: brands.length },
              ]
            ).map((tile) => {
              if (tile.kind === "placeholder") {
                return (
                  <button
                    key={`tile-${tile.i}`}
                    type="button"
                    className="home-tile home-tile--plain search-modal__brand search-modal__brand--placeholder"
                    style={{ "--i": tile.i }}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <span className="home-tile__media home-tile__media--pending" aria-hidden="true" />
                    <span className="home-tile__label" />
                  </button>
                );
              }
              if (tile.kind === "all") {
                return (
                  <button
                    key={`tile-${tile.i}`}
                    type="button"
                    className="home-tile home-tile--plain home-tile--all search-modal__brand"
                    style={{ "--i": tile.i }}
                    onClick={viewAllBrands}
                    // One random brand per visit (the owner, 2026-10-17: "it
                    // should just get a random one and go off that once
                    // everytime its hovered"). Swapping the page behind the
                    // glass makes the browser re-fire leave/enter on a still
                    // pointer, and each fresh enter drew another brand, so it
                    // kept changing; a leave only counts when the pointer is
                    // really outside the tile.
                    onPointerEnter={(e) => {
                      if (e.pointerType !== "mouse" || allBrandsVisit.current) return;
                      allBrandsVisit.current = true;
                      previewRandomBrand();
                    }}
                    onPointerLeave={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
                      if (!inside) allBrandsVisit.current = false;
                    }}
                    onFocus={(e) => { if (e.currentTarget.matches(":focus-visible")) previewRandomBrand(); }}
                  >
                    <span className="home-tile__media" aria-hidden="true" />
                    <span className="home-tile__label">All brands</span>
                  </button>
                );
              }
              const { brand } = tile;
              return (
                <button
                  key={`tile-${tile.i}`}
                  type="button"
                  className={`home-tile search-modal__brand${brand.url ? "" : " home-tile--plain"}`}
                  style={{ "--i": tile.i }}
                  onClick={() => goToBrand(brand.name)}
                  onPointerEnter={(e) => { if (e.pointerType === "mouse") previewBrand(brand.name); }}
                  onFocus={(e) => { if (e.currentTarget.matches(":focus-visible")) previewBrand(brand.name); }}
                >
                  <span
                    className={`home-tile__media${brand.url && !loadedTileUrls.has(brand.url) ? " home-tile__media--pending" : ""}`}
                    style={brand.url ? { backgroundImage: `url(${brand.url})` } : undefined}
                    aria-hidden="true"
                  />
                  <span className="home-tile__label">{brand.name}</span>
                </button>
              );
            })}
          </nav>
        ) : (
        <>
        <form
          ref={formRef}
          className={`search-modal__form${loading ? " search-modal__form--loading" : ""}`}
          role="search"
          onSubmit={submit}
          aria-busy={loading}
        >
          {loading ? (
            <span className="search-modal__loading" aria-live="polite">{query.trim()}</span>
          ) : (
            <input
              ref={inputRef}
              type="search"
              enterKeyHint="search"
              className="search-modal__input"
              placeholder="Search"
              aria-label="Search the catalog"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <button
            type="submit"
            className={`search-modal__go${hasQuery && !loading ? " search-modal__go--shown" : ""}`}
            aria-label="Search"
            tabIndex={hasQuery && !loading ? 0 : -1}
            aria-hidden={!(hasQuery && !loading)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          </button>
        </form>
        {/* Live matches for the text so far, in their own scroll box so the
            bar above never moves. Each is the product itself; the back arrow
            from there returns to the full results for this text. */}
        <div className="search-modal__list" ref={listRef} onScroll={handleListScroll}>
        <div
          className={`search-modal__results${showHits && hits.length ? " search-modal__results--shown" : ""}`}
          aria-live="polite"
        >
          {showHits && hits.map((item) => {
            const image = coverOf(item);
            const price = item.price_usd != null ? formatDisplayPrice(item.price_usd, "USD") : "";
            return (
              <Link
                key={item.id}
                to={`/featured/item/${item.id}`}
                state={{ from: `/featured/search?q=${encodeURIComponent(query.trim())}` }}
                className="search-modal__hit"
                onClick={close}
              >
                <span
                  className="search-modal__hit-img"
                  style={image ? { backgroundImage: `url(${image})` } : undefined}
                  aria-hidden="true"
                />
                <span className="search-modal__hit-name">{item.title || "Untitled"}</span>
                {price && <span className="search-modal__hit-price">{price}</span>}
              </Link>
            );
          })}
        </div>
        {/* Held rather than blank: without this the list empties the moment
            you type and stays empty for the settle, which reads as "nothing
            matches" instead of "still looking". */}
        {settling && !loading && (
          <p className="search-modal__empty search-modal__empty--settling">Searching…</p>
        )}
        {showHits && items && items.length > 0 && hits.length === 0 && (
          <p className="search-modal__empty">No matches</p>
        )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
